import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Who we are",
    body: [
      "Axiom Property Advisory (\"Axiom\", \"we\", \"us\") is an Australian property advisory business. We provide research, modelling and guidance to help clients make informed property decisions. We are an advisor, not a bank, real-estate agent, mortgage broker, or financial planner.",
      "Where we provide general information about financial products, that information is general in nature only. It does not take into account your personal objectives, financial situation, or needs. You should seek independent financial, legal, and tax advice before making any investment decision.",
    ],
  },
  {
    h: "2. Our service",
    body: [
      "Subscription plans provide access to property reports, market commentary, and support as described on the pricing page. Specific deliverables (number of reports, response times, etc.) are set out in the plan you choose.",
      "Off-market deal information is provided as research only. We do not act as your agent in any transaction. You remain solely responsible for the decision to buy, sell, hold, or finance any property.",
    ],
  },
  {
    h: "3. Subscriptions, billing and cancellation",
    body: [
      "Subscriptions are billed in advance on either a monthly or annual basis depending on the plan you select. Payments are processed by Stripe. By subscribing you authorise us to charge your chosen payment method each billing cycle until you cancel.",
      "Each subscription is a single, separate agreement between Axiom and the individual subscriber. Subscriptions cannot be combined, pooled, or shared across multiple users or entities.",
      "You may cancel your subscription at any time from your account dashboard or by emailing hello@axiompropertyadvisory.com.au. Cancellation takes effect at the end of the current billing period. We do not generally provide refunds for partial periods, except where required by Australian Consumer Law.",
      "Prices are shown in AUD on the website and charged in AUD.",
    ],
  },
  {
    h: "4. Single-User, Personal Use Licence",
    body: [
      "The Service is provided for personal use only and is intended for individual consumers acting in their own personal capacity.",
      "By subscribing, you agree that your account is for use solely by the individual who created the account. The Service must not be used by, on behalf of, or for the benefit of any company, business entity, partnership, trust, organisation, or group of users.",
      "You must not share, transfer, sell, sublicense, or otherwise allow any third party to access your account, login credentials, or any materials or reports generated through the Service.",
      "We may reasonably monitor account usage to ensure compliance with this clause.",
      "If we reasonably believe that your account is being used in breach of this clause, we may suspend or cancel your subscription. Where practicable, we will provide notice before doing so, except where immediate action is required to protect the integrity or security of the Service.",
      "If your subscription is cancelled due to a breach of these Terms, we may refuse or limit future access to the Service.",
    ],
  },
  {
    h: "5. No financial product advice",
    body: [
      "Nothing on this site, in our reports, or in our communications constitutes personal financial product advice, an offer to sell, or a recommendation to buy any specific property or financial product. Past performance is not a reliable indicator of future performance.",
      "Quoted rental yields, returns, growth rates, and similar figures are estimates only, based on assumptions stated in the relevant report. Actual results will vary.",
    ],
  },
  {
    h: "6. Your responsibilities",
    body: [
      "You agree to provide accurate information when signing up and to keep your account details secure. You are responsible for activity that occurs under your account.",
      "Our reports and content are licensed for your personal use only (see clause 4). You may not redistribute, resell, or publish them without our written consent.",
    ],
  },
  {
    h: "7. Consumer Law Compliance",
    body: [
      "Nothing in these Terms is intended to exclude, restrict, or modify any rights or remedies you may have under the Australian Consumer Law (Competition and Consumer Act 2010 (Cth)), including consumer guarantees that cannot be excluded by law.",
      "To the extent permitted by law, our liability for breach of any non-excludable guarantees is limited, at our option, to resupply of the Service or payment of the cost of resupply.",
      "Subject to the above, and to the maximum extent permitted by law, Axiom and its advisors are not liable for any indirect, incidental, consequential, or loss-of-profit damages arising from your use of our services or any decision made on the basis of information we provide.",
    ],
  },
  {
    h: "8. Privacy",
    body: [
      "We collect only the information you provide to us — your name, email, and any details you choose to share. We use this information to deliver our service, respond to enquiries, and communicate updates. We never sell or rent your data to third parties.",
      "You can request access to or deletion of your data at any time by emailing hello@axiompropertyadvisory.com.au.",
    ],
  },
  {
    h: "9. Changes to these terms",
    body: [
      "We may update these terms from time to time. We'll notify subscribers of material changes by email. Continued use of the service after a change takes effect constitutes acceptance of the updated terms.",
    ],
  },
  {
    h: "10. Governing law",
    body: [
      "These terms are governed by the laws of New South Wales, Australia. Any dispute will be subject to the exclusive jurisdiction of the courts of New South Wales.",
    ],
  },
  {
    h: "11. Contact",
    body: [
      "Questions about these terms? Email hello@axiompropertyadvisory.com.au.",
    ],
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-ink text-white grain">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <Link
          to="/"
          data-testid="terms-back-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-[11px] tracking-[0.3em] uppercase mb-12 transition-colors"
        >
          <ArrowLeft size={14} />
          Back to home
        </Link>

        <div className="data-label mb-6 text-gold">/ Legal</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight mb-6">
          Terms &amp; Conditions
        </h1>
        <p className="text-white/55 font-mono text-xs tracking-[0.2em] uppercase mb-16">
          Last updated · {new Date().getFullYear()}
        </p>

        <div className="border-t border-white/10 pt-12 space-y-14">
          {SECTIONS.map((s, i) => (
            <section key={s.h} data-testid={`terms-section-${i}`}>
              <h2 className="font-display text-2xl sm:text-3xl mb-5 leading-tight">
                {s.h}
              </h2>
              <div className="space-y-4 text-white/75 text-[15px] leading-relaxed">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 font-mono text-[11px] tracking-[0.25em] uppercase text-white/40">
          © {new Date().getFullYear()} Axiom Property Advisory · ABN forthcoming
        </div>
      </div>
    </div>
  );
}
